import type {
  ResponseAcademicRisk,
  SemesterAcademicRisk,
} from '@/domains/academicRisk/academicRiskStudent.entity';
import type { SemesterKey } from '@/domains/shared';

/**
 * Use Case para filtrar estudiantes por año de última matrícula en Academic Risk
 * Cumple SRP: Solo se encarga de filtrar por año válido
 *
 * Responsabilidad: Incluir solo estudiantes cuyo año de última matrícula
 * sea igual al año actual. Excluye registros con valor nulo.
 *
 * Similar a FilterStudentsByCurrentYearUseCase pero adaptado para la estructura
 * anidada año > semestre > estudiantes de Academic Risk.
 */
export class FilterStudentsByCurrentYearAcademicRiskUseCase {
  /**
   * Obtiene el año actual del sistema
   * @returns Año actual (ej: 2026)
   */
  private getCurrentYear(): number {
    return new Date().getFullYear();
  }

  /**
   * Filtra estudiantes de riesgo académico incluyendo solo aquellos cuyo año
   * de última matrícula sea igual al año actual. Excluye registros con
   * ano_ultima_matricula nulo.
   *
   * Mantiene la estructura jerárquica año > semestre > estudiantes
   *
   * @param response - Respuesta completa de la API con estructura anidada
   * @returns Respuesta filtrada con solo estudiantes del año actual
   *
   * @example
   * const response = {
   *   data: {
   *     "2025": {
   *       "1": [
   *         { ano_ultima_matricula: 2024, ... }, // Excluido
   *         { ano_ultima_matricula: 2026, ... }, // Incluido (si año actual es 2026)
   *         { ano_ultima_matricula: null, ... }  // Excluido
   *       ]
   *     }
   *   }
   * };
   * const filtered = execute(response); // Solo 2026
   */
  execute(response: ResponseAcademicRisk): ResponseAcademicRisk {
    if (!response?.data) {
      return { data: {} };
    }

    const currentYear = this.getCurrentYear();
    const filteredYears: ResponseAcademicRisk['data'] = {};

    for (const [year, semesters] of Object.entries(response.data)) {
      filteredYears[year] = {} as SemesterAcademicRisk;

      for (const [semester, students] of Object.entries(semesters)) {
        filteredYears[year][semester as SemesterKey] = students.filter(
          (student) =>
            student.ano_ultima_matricula !== null &&
            student.ano_ultima_matricula === currentYear,
        );
      }
    }

    return { data: filteredYears };
  }
}
